import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { object, string } from "yup";

import { EditButton } from "common/components/EditButton/EditButton";
import { TextField } from "common/components/TextField/TextField";
import { useAppDialog } from "hooks/useAppDialog";
import { useUpdateSelectedOrganizationApiToken } from "hooks/useUpdateSelectedOrganizationApiToken";
import { OrganizationApiToken } from "types/Organization";

interface EditSettingProps {
  row: Record<string, string>;
  token: OrganizationApiToken;
}

interface FormValues {
  key: string;
  value: string;
}

export const EditSetting = ({ row, token }: EditSettingProps): JSX.Element => {
  const { openDialog, Dialog, dialogProps, closeDialog } = useAppDialog();
  const { t } = useTranslation();
  const { isSuccess, isLoading, onUpdateApiToken } =
    useUpdateSelectedOrganizationApiToken({
      tokenId: token.id,
    });
  const formRef = useRef<FormikProps<FormValues>>(null);

  const onSubmit = () => {
    formRef.current?.handleSubmit();
  };

  useEffect(() => {
    if (isSuccess) {
      closeDialog();
    }
  }, [isSuccess, closeDialog]);

  const initialValues = useMemo(
    () => ({ key: row.key, value: row.value }),
    [row]
  );
  const onFormSubmit = useCallback(
    ({ key, value }: FormValues) => {
      const { [row.key]: _, ...settings } = token.settings as Record<
        string,
        string
      >;
      return onUpdateApiToken({
        id: token.id,
        name: token.name,
        description: token.description,
        permissions: token.permissions.map((permission) => permission),
        settings: {
          ...settings,
          [key]: value,
        },
      });
    },
    [token, row, onUpdateApiToken]
  );

  const validationSchema = useMemo(
    () =>
      object().shape({
        key: string().required(t("Required")),
        value: string().required(t("Required")),
      }),
    [t]
  );

  return (
    <>
      <EditButton onClick={openDialog} />
      <Dialog
        title={t("EditSetting")}
        loading={isLoading}
        onSubmit={onSubmit}
        actionText={t("Save")}
        maxWidth="sm"
        {...dialogProps}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={onFormSubmit}
          innerRef={formRef}
          validationSchema={validationSchema}
        >
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField name="key" label={t("Name")} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField name="value" label={t("Value")} fullWidth />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </Dialog>
    </>
  );
};
